import React from 'react';
import { useFormTheme } from '../../../contexts/FormThemeContext';
import { TextField, Button, Box, Typography, FormControl, InputLabel, Select, MenuItem, IconButton, InputAdornment } from '@mui/material';
import ColorPicker from './ColorPicker'; // Ensure the correct import path
import ClearIcon from '@mui/icons-material/Clear';

const ButtonStyleSettings = () => {
  const {
    submitButtonStyle,
    handleStyleChange,
    defaultSubmitButtonStyle 
  } = useFormTheme();

  if (!submitButtonStyle) return null;

  const updateStyle = (prop, value) => {
    // Check if value is a color object and handle accordingly
    if (prop === 'fontSize' || prop === 'width' || prop === 'height' || prop === 'borderRadius') {
      handleStyleChange(prop, value ? `${value}px` : value); // Append 'px' if the value is provided
    } else {
      handleStyleChange(prop, value.hex || value);
    }
    console.log('[ButtonStyleSettings] Updating style for:', prop, 'with value:', value); // Add this for debugging
  };

  const resetStyle = (prop) => {
    handleStyleChange(prop, defaultSubmitButtonStyle[prop]);
  };

  // Mapping for font weights to their names
  const fontWeightOptions = {
    '100': 'Thin',
    '200': 'Extra Light',
    '300': 'Light',
    '400': 'Normal',
    '500': 'Medium',
    '600': 'Semi-Bold',
    '700': 'Bold',
    '800': 'Extra Bold',
    '900': 'Black'
  };

  // Labels for the ColorPicker, directly related to button styling
  const colorLabelMap = {
    backgroundColor: 'Background',
    textColor: 'Text',
    borderColor: 'Border'
  };

  // Labels for sizing fields
  const sizeLabelMap = {
    width: 'Width',
    height: 'Height',
    borderRadius: 'Radius'
  };

  // Labels for font styling
  const fontLabelMap = {
    fontSize: 'Font Size',
    fontWeight: 'Font Weight'
  };

  return (
    <Box sx={{ padding: '0 2px 0 2px' }}> 
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        marginBottom: 1,
      }}>
        {/* Color pickers for button styling */}
        {Object.entries(colorLabelMap).map(([prop, label]) => (
          <Box key={prop} sx={{
            width: '32%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 2,
          }}>
            <ColorPicker 
              color={submitButtonStyle[prop]} 
              onChange={(color) => updateStyle(prop, color)} 
            />
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 300, fontSize: '0.75rem' }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
      {/* Row for width, height, and border radius fields */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        marginBottom: 1,
      }}>
        {Object.entries(sizeLabelMap).map(([prop, label]) => (
          <Box key={prop} sx={{
            width: '32%',
            mb: 1,
          }}>
            <TextField
              label={label}
              type="number"
              value={submitButtonStyle[prop] ? submitButtonStyle[prop].replace('px', '') : ''} // Keep the value empty to show placeholder
              onChange={(e) => updateStyle(prop, e.target.value)}
              variant="outlined"
              fullWidth
              size="small"
              placeholder={submitButtonStyle[prop] ? '' : defaultSubmitButtonStyle[prop]} // Only show placeholder when there's no value
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => resetStyle(prop)}
                      edge="end"
                      size="small"
                      sx={{ marginRight: '-12px' }} // Reducing right padding of the icon button
                    >
                      <ClearIcon fontSize="inherit" style={{ fontSize: '14px' }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        ))}
      </Box>
      {/* Select for button alignment */}
      <Box sx={{ width: '100%', mb: 1 }}>
        <FormControl fullWidth>
          <InputLabel>Button Alignment</InputLabel>
          <Select
            value={submitButtonStyle.alignment || 'center'}
            onChange={(e) => updateStyle('alignment', e.target.value)}
            label="Button Alignment"
            size="small"
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {/* Box to wrap the submit button label text field */}
      <Box sx={{ width: '100%', mb: 1 }}>
        <TextField
          label="Button Label"
          value={submitButtonStyle.label || 'Submit'}
          onChange={(e) => updateStyle('label', e.target.value)}
          margin="normal"
          fullWidth
          variant="outlined"
          placeholder="Submit"
        />
      </Box>
      {/* Row for font size and font weight fields */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        marginBottom: 2,
      }}>
        <Box sx={{ width: '48%', mb: 1 }}>
          <TextField
            label="Font Size"
            type="number"
            value={submitButtonStyle.fontSize ? submitButtonStyle.fontSize.replace('px', '') : ''} // Keep the value empty to show placeholder
            onChange={(e) => updateStyle('fontSize', e.target.value)}
            variant="outlined"
            fullWidth
            size="small"
            placeholder={submitButtonStyle.fontSize ? '' : defaultSubmitButtonStyle.fontSize} // Only show placeholder when there's no value
          />
        </Box>
        <Box sx={{ width: '48%', mb: 1 }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Font Weight</InputLabel>
            <Select
              label="Font Weight"
              value={submitButtonStyle.fontWeight || defaultSubmitButtonStyle.fontWeight} 
              onChange={(e) => updateStyle('fontWeight', e.target.value)}
              renderValue={(value) => fontWeightOptions[value]}
            >
              {Object.entries(fontWeightOptions).map(([value, name]) => (
                <MenuItem key={value} value={value} style={{ fontWeight: value }}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default ButtonStyleSettings;
